import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { COLORS } from "../assets/theme";
import CustomDialog from "./CustomDialog";
import CustomLabel from "./CustomLabel";
import CustomTextField from "./CustomTextField";
import CustomButton from "./CustomButton";
import { getUserId } from "../services/authService";
import {
  requestRevenuePin,
  verifyRevenuePin,
} from "../services/RevenueAccessService";
import { setCachedPinAccess } from "../utils/pinAccessCache";

interface PinAccessDialogProps {
  open: boolean;
  onClose: () => void;
  onVerified?: () => void;
  title?: string;
  description?: string;
  /** Temporary local-only PIN. Omit this to use the finance PIN API. */
  staticPin?: string;
}

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const resolvePinMessage = (payload: any, fallback: string): string => {
  return (
    pickNonEmptyString(
      payload?.message,
      payload?.msg,
      payload?.responseMessage,
      payload?.statusMessage,
      payload?.status_text,
      payload?.status,
      payload
    ) || fallback
  );
};

const resolvePinError = (error: any, fallback: string): string => {
  return (
    pickNonEmptyString(
      error?.response?.data?.message,
      error?.response?.data?.error,
      error?.response?.data?.msg,
      error?.response?.data?.responseMessage,
      error?.response?.data?.statusMessage,
      error?.message,
      error?.response?.data
    ) || fallback
  );
};

const isPinResponseSuccess = (payload: any): boolean => {
  if (!payload) return true;
  if (payload.success === false) return false;
  if (payload.status === false) return false;
  if (payload.error) return false;
  const statusText =
    typeof payload.status === "string" ? payload.status.toLowerCase() : "";
  if (statusText === "error" || statusText === "failed") return false;
  return true;
};

const isPinVerified = (payload: any): boolean => {
  if (!isPinResponseSuccess(payload)) return false;
  if (payload?.verified === false) return false;
  return true;
};

const PinAccessDialog: React.FC<PinAccessDialogProps> = ({
  open,
  onClose,
  onVerified,
  title = "Finance access",
  description = "Enter the PIN to access finance. Use Send PIN if you need it.",
  staticPin,
}) => {
  const [pin, setPin] = React.useState("");
  const [pinError, setPinError] = React.useState("");
  const [pinMessage, setPinMessage] = React.useState("");
  const [pinLoading, setPinLoading] = React.useState<"send" | "verify" | null>(
    null
  );

  React.useEffect(() => {
    if (open) return;
    setPin("");
    setPinError("");
    setPinMessage("");
    setPinLoading(null);
  }, [open]);

  const handleClose = () => {
    if (pinLoading) return;
    onClose();
  };

  const handleSendPin = async () => {
    if (pinLoading) return;
    const userId = getUserId();
    if (!userId) {
      toast.error("User session missing. Please log in again.", {
        position: "top-right",
      });
      return;
    }
    setPinError("");
    setPinMessage("");
    setPinLoading("send");
    try {
      const response = await requestRevenuePin(userId);
      if (!isPinResponseSuccess(response)) {
        const message = resolvePinMessage(response, "Failed to send PIN.");
        setPinError(message);
        toast.error(message, { position: "top-right" });
        return;
      }
      const message = resolvePinMessage(response, "PIN sent to your email.");
      setPinMessage(message);
      toast.success(message, { position: "top-right" });
    } catch (error: any) {
      const message = resolvePinError(error, "Failed to send PIN.");
      setPinError(message);
      toast.error(message, { position: "top-right" });
    } finally {
      setPinLoading(null);
    }
  };

  const handleVerifyPin = async () => {
    if (pinLoading) return;
    const trimmedPin = pin.trim();
    if (!trimmedPin) {
      setPinError("Please enter the PIN.");
      return;
    }
    setPinError("");
    setPinMessage("");

    if (staticPin !== undefined) {
      if (trimmedPin !== staticPin) {
        setPinError("Invalid PIN. Please try again.");
        return;
      }

      toast.success("Access unlocked.", { position: "top-right" });
      onVerified?.();
      onClose();
      return;
    }

    const userId = getUserId();
    if (!userId) {
      setPinError("User session missing. Please log in again.");
      return;
    }
    setPinLoading("verify");
    try {
      const response = await verifyRevenuePin(userId, trimmedPin);
      if (!isPinVerified(response)) {
        const message = resolvePinMessage(
          response,
          "Invalid PIN. Please try again."
        );
        setPinError(message);
        return;
      }
      const successMessage = resolvePinMessage(
        response,
        "Finance access unlocked."
      );
      setCachedPinAccess(true);
      toast.success(successMessage, { position: "top-right" });
      onVerified?.();
      onClose();
    } catch (error: any) {
      const message = resolvePinError(error, "Failed to verify PIN.");
      setPinError(message);
    } finally {
      setPinLoading(null);
    }
  };

  const handlePinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = event.target.value.replace(/\D/g, "");
    setPin(sanitized);
    if (pinError) setPinError("");
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={title}
      actions={
        <>
          <CustomButton
            label="Cancel"
            onClick={handleClose}
            variant="text"
            textColor={COLORS.MainColor}
            disabled={Boolean(pinLoading)}
          />
          {staticPin === undefined && (
            <CustomButton
              label={pinLoading === "send" ? "Sending..." : "Send PIN"}
              onClick={handleSendPin}
              variant="outlined"
              textColor={COLORS.MainColor}
              borderColor={COLORS.MainColor}
              loading={pinLoading === "send"}
              disabled={pinLoading === "verify"}
            />
          )}
          <CustomButton
            label={pinLoading === "verify" ? "Verifying..." : "Verify PIN"}
            onClick={handleVerifyPin}
            variant="contained"
            bgColor={COLORS.MainColor}
            loading={pinLoading === "verify"}
            disabled={!pin.trim() || pinLoading === "send"}
          />
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CustomLabel
          text={description}
          fontSize="0.9rem"
          color={COLORS.typographyColor}
        />
        <CustomTextField
          label="PIN"
          value={pin}
          onChange={handlePinChange}
          fullWidth
          size="small"
          type="password"
          showPasswordToggle
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 8,
          }}
          error={Boolean(pinError)}
          helperText={pinError || ""}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleVerifyPin();
            }
          }}
          autoFocus
        />
        {pinLoading === "send" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <CustomLabel
              text="Sending PIN..."
              fontSize="0.85rem"
              color={COLORS.typographyColor}
            />
          </Box>
        ) : pinMessage && !pinError ? (
          <CustomLabel
            text={pinMessage}
            fontSize="0.85rem"
            color={COLORS.SUCCESS}
          />
        ) : null}
      </Box>
    </CustomDialog>
  );
};

export default PinAccessDialog;
